import React, { useEffect, useRef, useState } from 'react';

// Inline WhatsApp-style bubble for mobile overlay
function MobileBubble({ text, side, opacity, slideIn, bottom = 20 }: { text: string; side: 'left' | 'right'; opacity: number; slideIn: boolean; bottom?: number }) {
  const isRight = side === 'right';
  const bubbleColor = isRight ? '#005C4B' : '#1F2C34';
  return (
    <div style={{
      position: 'absolute',
      bottom,
      ...(isRight ? { right: 8 } : { left: 8 }),
      transform: slideIn ? 'translateX(0) scale(1)' : (isRight ? 'translateX(12px)' : 'translateX(-12px)') + ' scale(0.93)',
      opacity,
      transition: 'opacity 0.4s ease, transform 0.4s cubic-bezier(0.34,1.56,0.64,1)',
      zIndex: 4,
      pointerEvents: 'none',
      display: 'flex',
      flexDirection: 'column',
      alignItems: isRight ? 'flex-end' : 'flex-start',
      gap: 3,
      maxWidth: 140,
    }}>
      {!isRight && (
        <span style={{ fontSize: 9, fontWeight: 600, color: '#e8365d', paddingLeft: 10, fontFamily: "'Segoe UI', sans-serif" }}>
          anonymous
        </span>
      )}
      <div style={{
        background: bubbleColor,
        borderRadius: isRight ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
        padding: '7px 10px 5px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
        maxWidth: 150,
      }}>
        <p style={{ margin: 0, fontSize: 11, color: '#E9EDEF', lineHeight: 1.5, wordBreak: 'break-word', fontFamily: "'Segoe UI', sans-serif" }}>
          {text}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, marginTop: 3 }}>
          <span style={{ fontSize: 8, color: 'rgba(233,237,239,0.45)', fontFamily: "'Segoe UI', sans-serif" }}>via Yhuu · now</span>
          <svg width={12} height={7} viewBox="0 0 16 9" fill="none">
            <path d="M1 4.5L5 8.5L11 1.5" stroke="#53BDEB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M6 4.5L10 8.5L16 1.5" stroke="#53BDEB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
  );
}

const BUBBLE_TEXTS = [
  ['be honest — do you miss me?', 'did you ever like me, like that?', "do you think about me when we don't talk?"],
  ['would you date me if things were different?', "why'd you really pull away?", 'if I asked you out, what would you say?'],
  ["what's your actual first impression of me?", 'be real — am I annoying you?', 'would you be honest with me?'],
  ['do you actually like me or are you just bored?', 'what do you really think of me?', 'would your friends date me?'],
];

const POOL = [
  '/photos/Screenshot 2026-05-27 111842 - Copy.png',
  '/photos/Screenshot 2026-05-27 111945.png',
  '/photos/Screenshot 2026-05-27 115951.png',
  '/photos/reaction-guy.jpg.jpg',
  '/photos/reaction-woman-shocked.jpg.jpg',
  '/photos/reaction-woman-suspicious.jpg.jpg',
];

let usedIdx: number[] = [];
function pickPhoto(exclude?: string) {
  const excI = exclude ? POOL.indexOf(exclude) : -1;
  const avail = POOL.map((_, i) => i).filter(i => !usedIdx.includes(i) && i !== excI);
  const pool = avail.length > 0 ? avail : POOL.map((_, i) => i).filter(i => i !== excI);
  const pick = pool[Math.floor(Math.random() * pool.length)];
  usedIdx = [...usedIdx.slice(-2), pick];
  return POOL[pick];
}

const SIDES = [
  { side: 'left',  rotate: -6, depth: 1.0, delay: 150 },
  { side: 'right', rotate:  7, depth: 0.8, delay: 400 },
] as const;

export default function FloatingPhotos() {
  const [photos, setPhotos]     = useState<string[]>(() => [POOL[0], POOL[1]]);
  const [visible, setVisible]   = useState([false, false]);
  const [pressed, setPressed]   = useState([false, false]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [bubbleTextIdx, setBubbleTextIdx] = useState([0, 0, 0, 0]);
  const [bubbleOpacity, setBubbleOpacity] = useState([0, 0, 0, 0]);
  const [bubbleSlide, setBubbleSlide]     = useState([false, false, false, false]);
  const containerRef = useRef<HTMLDivElement>(null);
  const pointerRef   = useRef({ x: 0, y: 0 });
  const cardRefs     = useRef<(HTMLDivElement | null)[]>([]);
  const rafRef       = useRef<number | null>(null);
  const startTime    = useRef(performance.now());

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    SIDES.forEach((_, i) =>
      setTimeout(() => setVisible(prev => { const n = [...prev]; n[i] = true; return n; }), SIDES[i].delay)
    );
  }, []);

  useEffect(() => {
    const parent = containerRef.current?.parentElement;
    if (!parent) return;
    const onMove = (e: MouseEvent) => {
      const r = parent.getBoundingClientRect();
      pointerRef.current = {
        x: ((e.clientX - r.left) / r.width  - 0.5) * 2,
        y: ((e.clientY - r.top)  / r.height - 0.5) * 2,
      };
    };
    const onLeave = () => { pointerRef.current = { x: 0, y: 0 }; };
    parent.addEventListener('mousemove', onMove);
    parent.addEventListener('mouseleave', onLeave);
    return () => { parent.removeEventListener('mousemove', onMove); parent.removeEventListener('mouseleave', onLeave); };
  }, []);

  useEffect(() => {
    const BREATHE = [3600, 3100];
    function animate(now: number) {
      const { x: mx, y: my } = pointerRef.current;
      cardRefs.current.forEach((el, i) => {
        if (!el || !visible[i]) return;
        const card = SIDES[i];
        const t   = now - startTime.current;
        const dy  = Math.sin((t / BREATHE[i]) * Math.PI * 2) * 8;
        const px  = mx * 16 * card.depth;
        const py  = my * 10 * card.depth;
        const rot = card.rotate + mx * 2.5 * card.depth;
        const shadowY    = 14 + dy * 0.4 + my * 6;
        const shadowBlur = 32 + Math.abs(mx) * 12;
        el.style.transform = `translateX(${px}px) translateY(${py + dy}px) rotate(${rot}deg)`;
        el.style.boxShadow = `0 ${shadowY}px ${shadowBlur}px rgba(0,0,0,${0.2 + Math.abs(mx) * 0.07})`;
      });
      rafRef.current = requestAnimationFrame(animate);
    }
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [visible]);

  useEffect(() => {
    if (!isMobile) return;
    const show = (i: number) => {
      setBubbleSlide(p => { const n=[...p]; n[i]=true; return n; });
      setBubbleOpacity(p => { const n=[...p]; n[i]=1; return n; });
    };
    const hide = (i: number) => {
      setBubbleSlide(p => { const n=[...p]; n[i]=false; return n; });
      setBubbleOpacity(p => { const n=[...p]; n[i]=0; return n; });
    };
    const STEP = 2000;

    // Initial: show slots 0 and 1 staggered
    setTimeout(() => show(0), 600);
    setTimeout(() => show(1), 600 + STEP);

    let tick = 2;
    const cycle = setInterval(() => {
      const incoming = tick % 4;
      const outgoing = (tick + 2) % 4;
      // Bump text on incoming slot
      setBubbleTextIdx(p => {
        const n = [...p];
        n[incoming] = (n[incoming] + 1) % BUBBLE_TEXTS[incoming].length;
        return n;
      });
      show(incoming);
      hide(outgoing);
      tick++;
    }, STEP);
    return () => clearInterval(cycle);
  }, [isMobile]);

  useEffect(() => {
    const timers: ReturnType<typeof setInterval>[] = [];
    SIDES.forEach((_, i) => {
      timers.push(setInterval(() => {
        setVisible(prev => { const n = [...prev]; n[i] = false; return n; });
        setTimeout(() => {
          setPhotos(prev => { const n = [...prev]; n[i] = pickPhoto(prev[i]); return n; });
          setVisible(prev => { const n = [...prev]; n[i] = true; return n; });
        }, 600);
      }, 6000 + i * 1200 + Math.random() * 2000));
    });
    return () => timers.forEach(clearInterval);
  }, []);

  // ── MOBILE: render as normal flow row so photos are always visible ──
  if (isMobile) {
    return (
      <>
        <style>{`
          @keyframes nglIn  { 0%{opacity:0;transform:translateY(20px) scale(0.9)} 100%{opacity:1;transform:translateY(0) scale(1)} }
          @keyframes nglOut { from{opacity:1} to{opacity:0} }
          .ngl-in  { animation: nglIn  0.5s ease forwards; }
          .ngl-out { animation: nglOut 0.4s ease forwards; }
        `}</style>
        <div ref={containerRef} style={{ position: 'relative', display: 'flex', gap: 12, justifyContent: 'center', marginTop: 32, overflow: 'visible' }}>
          <MobileBubble text={BUBBLE_TEXTS[0][bubbleTextIdx[0]]} side="left" opacity={bubbleOpacity[0]} slideIn={bubbleSlide[0]} bottom={20} />
          <MobileBubble text={BUBBLE_TEXTS[1][bubbleTextIdx[1]]} side="right" opacity={bubbleOpacity[1]} slideIn={bubbleSlide[1]} bottom={20} />
          <MobileBubble text={BUBBLE_TEXTS[2][bubbleTextIdx[2]]} side="left" opacity={bubbleOpacity[2]} slideIn={bubbleSlide[2]} bottom={110} />
          <MobileBubble text={BUBBLE_TEXTS[3][bubbleTextIdx[3]]} side="right" opacity={bubbleOpacity[3]} slideIn={bubbleSlide[3]} bottom={110} />
          {SIDES.map((card, i) => (
            <div
              key={i}
              ref={el => { cardRefs.current[i] = el; }}
              className={visible[i] ? 'ngl-in' : 'ngl-out'}
              onTouchStart={() => setPressed(prev => { const n = [...prev]; n[i] = true; return n; })}
              onTouchEnd={() => setPressed(prev => { const n = [...prev]; n[i] = false; return n; })}
              onTouchCancel={() => setPressed(prev => { const n = [...prev]; n[i] = false; return n; })}
              style={{
                width: 150,
                height: 210,
                borderRadius: 18,
                overflow: 'hidden',
                boxShadow: pressed[i] ? '0 20px 48px rgba(0,0,0,0.4)' : '0 12px 32px rgba(0,0,0,0.25)',
                transform: `rotate(${card.rotate}deg) scale(${pressed[i] ? 1.08 : 1})`,
                flexShrink: 0,
                transition: 'transform 0.18s cubic-bezier(0.34,1.4,0.64,1), box-shadow 0.18s ease',
                zIndex: pressed[i] ? 10 : 1,
              }}
            >
              <img src={photos[i]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block' }} />
            </div>
          ))}
        </div>
      </>
    );
  }

  // ── DESKTOP: absolute positioned floating photos ──
  return (
    <>
      <style>{`
        @keyframes nglIn  { 0%{opacity:0;transform:translateY(28px) scale(0.88)} 60%{transform:translateY(-5px) scale(1.03)} 80%{transform:translateY(3px) scale(0.98)} 100%{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes nglOut { from{opacity:1;transform:scale(1)} to{opacity:0;transform:scale(0.9) translateY(12px)} }
        .ngl-in  { animation: nglIn  0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; }
        .ngl-out { animation: nglOut 0.45s ease forwards; }
      `}</style>

      <div ref={containerRef} aria-hidden="true" style={{
        position: 'absolute', inset: 0,
        pointerEvents: 'none', zIndex: 0,
        overflow: 'visible',
      }}>
        {SIDES.map((card, i) => (
          <div
            key={i}
            ref={el => { cardRefs.current[i] = el; }}
            className={visible[i] ? 'ngl-in' : 'ngl-out'}
            style={{
              position: 'absolute',
              top: '32%',
              ...(card.side === 'left' ? { left: -100 } : { right: -100 }),
              width: 260,
              height: 340,
              borderRadius: 20,
              overflow: 'hidden',
              boxShadow: '0 16px 40px rgba(0,0,0,0.22)',
              willChange: 'transform, box-shadow',
            }}
          >
            <img src={photos[i]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center', display: 'block', filter: 'brightness(1.05) contrast(1.06) saturate(1.1)' }} />
          </div>
        ))}
      </div>
    </>
  );
}
